import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

interface VentureCategory {
  id: string;
  coverImage: string;
}

const ventureCategories: VentureCategory[] = [
  {
    id: 'metal',
    coverImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/f70d1e93c788fdabf987c14c4087ebff.jpeg',
  },
  {
    id: 'fujifilm',
    coverImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/016a48ddb4fbcc2be7e8bdd854be9589.jpeg',
  },
  {
    id: 'fashion',
    coverImage: 'https://storage.readdy-site.link/project_files/e7a178d6-0560-413e-a55b-8af9dbae3f7b/a212845c-9a4f-4d1a-bd9f-ad3b754ed96c__20260404000200_104_51.jpg?v=3c81c369f091abfd2fb7114f974658e5',
  },
];

interface CategoryCardProps {
  category: VentureCategory;
  index: number;
}

function CategoryCard({ category, index }: CategoryCardProps) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  // 墨绿色系
  const greenAccent = '#3a7a52';
  const greenAccentBright = '#4e9e6a';
  const greenBorder = 'rgba(58,122,82,0.5)';
  const greenBorderDim = 'rgba(58,122,82,0.25)';
  const greenGlow = 'rgba(58,122,82,0.3)';
  const greenGlowSoft = 'rgba(58,122,82,0.12)';
  const overlayColor = 'rgba(58,122,82,0.04)';
  const overlayColorBottom = 'rgba(58,122,82,0.06)';

  const handleClick = () => {
    navigate(`/gallery/ventures/${category.id}`);
  };

  return (
    <div
      className="relative flex-1 overflow-hidden cursor-pointer group"
      style={{
        animationDelay: `${index * 150}ms`,
        borderRadius: '4px',
        transition: 'box-shadow 0.6s ease',
        boxShadow: hovered
          ? `0 0 18px 4px ${greenGlow}, 0 0 36px 8px ${greenGlowSoft}, inset 0 0 0 1px ${greenBorder}`
          : `0 0 0px 0px rgba(0,0,0,0), inset 0 0 0 1px ${greenBorderDim}`,
        aspectRatio: '3 / 4',
        minHeight: 0,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
    >
      {/* Cover Image */}
      <img
        src={category.coverImage}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        style={{
          objectPosition: category.id === 'metal' ? 'left center' : category.id === 'fashion' ? 'center 65%' : 'center top',
          transform: category.id === 'fashion' ? 'scale(1.28)' : undefined,
          transformOrigin: 'center 68%',
        }}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-black/5 transition-opacity duration-500" />

      {/* Accent glow border overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          transition: 'opacity 0.6s ease',
          opacity: hovered ? 1 : 0,
          background: `linear-gradient(to bottom, ${overlayColor} 0%, transparent 30%, transparent 70%, ${overlayColorBottom} 100%)`,
        }}
      />

      {/* Bottom area with decorative line */}
      <div className="absolute bottom-0 left-0 right-0 px-6 pb-7 pt-10 bg-gradient-to-t from-black/60 via-black/30 to-transparent">
        {/* Decorative line */}
        <div
          className="transition-all duration-500"
          style={{
            height: '1px',
            background: `linear-gradient(to right, ${greenAccent}e6, ${greenAccent}4d, transparent)`,
            width: hovered ? '85%' : '40%',
          }}
        />

        {/* View label */}
        <div
          className="flex items-center gap-2 mt-5 transition-all duration-500"
          style={{
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'translateY(0)' : 'translateY(8px)',
          }}
        >
          <i className="ri-image-line text-sm" style={{ color: greenAccent }}></i>
          <span
            className="font-cinzel text-xs tracking-widest"
            style={{ color: greenAccent }}
          >
            VIEW GALLERY
          </span>
        </div>
      </div>
    </div>
  );
}

export default function VenturesSubpage() {
  const navigate = useNavigate();

  const venturesJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Wei Chen – Ventures beyond Classical',
    description: 'Life outside the Opera lights. Metal, Fujifilm photography, and Fashion & Dance by baritone Wei Chen.',
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/gallery/ventures`,
    author: {
      '@type': 'Person',
      name: 'Wei Chen',
      jobTitle: 'Baritone',
    },
  };

  return (
    <div className="min-h-screen bg-deep-black">
      <SeoHead
        title="Wei Chen – Ventures beyond Classical | Metal, Photography & Fashion"
        description="Explore Wei Chen's creative ventures beyond classical opera: metal music, Fujifilm photography, and fashion & dance photography."
        keywords="Wei Chen, Ventures, Metal, Fujifilm Photography, Fashion Dance, Baritone"
        canonical="/gallery/ventures"
        jsonLd={venturesJsonLd}
      />
      <Navbar />

      {/* Hero Header */}
      <section className="relative pt-32 pb-10 px-6 border-b border-champagne-gold/20 overflow-hidden">
        {/* Wood Background - single image cover */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/edited_ebd3df2806cc3b5732f85b166c2a6d75_19c06641.jpg')`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
          }}
        />
        {/* Overlay to ensure text readability */}
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/30 via-black/20 to-black/35"></div>
        {/* Top & bottom edge darkening */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, transparent 22%, transparent 72%, rgba(0,0,0,0.45) 100%)',
          }}
        ></div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/gallery')}
            className="group flex items-center gap-2 mb-8 text-champagne-gold/70 hover:text-champagne-gold transition-colors duration-300 cursor-pointer whitespace-nowrap"
            style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}
          >
            <i className="ri-arrow-left-line text-lg transition-transform duration-300 group-hover:-translate-x-1"></i>
            <span className="font-cinzel text-sm tracking-wider">BACK TO GALLERY</span>
          </button>

          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-cinzel-decorative font-bold mb-4" style={{ color: '#3a7a52', textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}>
              Ventures beyond Classical
            </h1>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-16" style={{ backgroundColor: 'rgba(58,122,82,0.4)' }}></div>
              <div className="w-5 h-5 flex items-center justify-center" style={{ color: '#3a7a52', opacity: 0.6 }}>
                <i className="ri-camera-line text-sm"></i>
              </div>
              <div className="h-px w-16" style={{ backgroundColor: 'rgba(58,122,82,0.4)' }}></div>
            </div>
            <p className="font-cormorant italic text-lg md:text-xl" style={{ color: '#ede8d8', opacity: 0.75, textShadow: '0 1px 6px rgba(0,0,0,0.7)' }}>
              Life outside the Opera lights
            </p>
          </div>
        </div>
      </section>

      {/* Spacer between hero and category grid */}
      <div className="h-10 bg-[#111111]" />

      {/* Three-column Category Modules — 缩小整体宽度 */}
      <section className="pb-24 px-4 md:px-6 bg-[#111111]">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 md:gap-5">
            {ventureCategories.map((cat, i) => (
              <CategoryCard key={cat.id} category={cat} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}